import type { ComponentPropsWithoutRef } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { focus } from 'util/dom';
import { toNumber } from 'util/number';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import styled from 'styled-components';
import allResultsSearchIcon from 'assets/images/all-results-search-icon.svg';
import atlassianIntelligenceIconUrl from 'assets/images/atlassian-intelligence-icon-background.svg';
import enterIconUrl from 'assets/images/enter-icon.svg';
import {
    isHelpCenterAIAnswersEAPEnabled,
    isJsmVirtualAgentOnPortalEnabled,
    isUnifiedHelpInJSMEnabled,
} from 'feature-flags';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useRouter } from 'react-resource-router';
import type { State } from 'state';
import { getProjectId } from 'state/selectors/portal';
import { getAccountId } from 'state/selectors/user';
import { useConversationActions as useUnifiedHelpConversationActions } from 'unified-help-center-alpha/src/store/converse';
import { getProductIdentifierAttributes } from 'unified-help-center-alpha/src/utils/analytics/get-product-identifier-attributes';
import { ScreenName } from 'view/common/constants';
import { SEARCH_INPUT_FONT_SIZE } from 'view/common/styled';
import * as grid from 'view/styles/grid';
import * as mixins from 'view/styles/mixins';
import { useAnalyticsEvents, withAnalyticsContext } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import SearchIcon from '@atlaskit/icon/glyph/search';
import { SmartCardProvider } from '@atlaskit/link-provider';
import Popup from '@atlaskit/popup';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { WidthObserver } from '@atlaskit/width-detector';
import { OPERATIONAL_EVENT_TYPE, UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { ExperienceType } from '@atlassian/conversation-assistant';
import { CONVERSATION_PATH } from '@atlassian/help-center-common-component/constants';
import { VirtualAgentConversationDrawer } from '@atlassian/help-center-virtual-agent';
import { useActions as useVirtualAgentConversationActions } from '@atlassian/help-center-virtual-agent/src/controllers/conversation-store';
import { ConversationStateContainer } from '@atlassian/help-center-virtual-agent/src/controllers/conversation-store/container';
import { useVirtualAgentConversationProvider } from '@atlassian/help-center-virtual-agent/src/services/conversation-service';
import { useVirtualAgentAvailability } from '@atlassian/help-center-virtual-agent/src/services/virtual-agent-availability';
import { ConversationExperience } from '../../conversation';
import messages from './messages';

interface ExternalProps {
    portalId: string | undefined;
}

interface Props {
    accountId: string;
    portalId: number | undefined;
    projectId: number | undefined;
}

const SearchTextboxV2 = ({ accountId, portalId, projectId }: Props) => {
    di(useUnifiedHelpConversationActions, useVirtualAgentConversationActions, useRouter, useVirtualAgentAvailability);

    const [isChoiceOpen, setIsChoiceOpen] = useState(false);
    const [isConversationOpen, setIsConversationOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchBoxWidth, setSearchBoxWidth] = useState<number>();
    const [{ location }, { push }] = useRouter();
    const { formatMessage } = useIntl();
    const { startNewConversation, setProviderAuthStatus } = useUnifiedHelpConversationActions();
    const { addMessage: addVirtualAgentMessage } = useVirtualAgentConversationActions();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const scope = portalId === undefined ? 'helpCenter' : 'portal';
    const { isVirtualAgentAvailable } = useVirtualAgentAvailability({ projectId });
    const isAtlassianIntelligenceOptionShown: boolean =
        (isUnifiedHelpInJSMEnabled() && scope === 'helpCenter') ||
        (isJsmVirtualAgentOnPortalEnabled() && scope === 'portal' && isVirtualAgentAvailable);

    const openChoicesOnChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        setSearchQuery(newQuery);
        setIsChoiceOpen(newQuery.trim() !== '');
    }, []);

    const handleCrossClick = useCallback(() => {
        setSearchQuery('');
        setIsChoiceOpen(false);
    }, []);

    const onSeeSearchResults = useCallback(() => {
        if (searchQuery.trim() === '') {
            return;
        }

        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'selected',
            actionSubjectId: 'allResults',
            conversationScope: scope,
            ...(scope === 'helpCenter' ? getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP) : undefined),
        }).fire();
        push(`${location.pathname}?q=${searchQuery.trim()}`) as unknown as void;
    }, [searchQuery, createAnalyticsEvent, scope, push, location.pathname]);

    const onAskAtlassianIntelligence = useCallback(() => {
        if (searchQuery.trim() === '') {
            return;
        }

        createAnalyticsEvent({
            analyticsType: UI_EVENT_TYPE,
            action: 'selected',
            actionSubjectId: 'askAtlassianIntelligence',
            conversationScope: scope,
            ...(scope === 'helpCenter' ? getProductIdentifierAttributes(ExperienceType.UNIFIED_HELP) : undefined),
        }).fire();

        if (scope === 'helpCenter') {
            startNewConversation(searchQuery.trim(), { accountId });
            if (isHelpCenterAIAnswersEAPEnabled()) {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                push(CONVERSATION_PATH);
            } else {
                setIsConversationOpen(true);
            }
        } else {
            addVirtualAgentMessage(searchQuery.trim()).catch((err: Error) => {
                createAnalyticsEvent({
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    action: 'failed',
                    actionSubject: 'virtualAgentConversation',
                    attributes: {
                        error: err.message,
                    },
                }).fire();
            });
            setIsConversationOpen(true);
        }
    }, [searchQuery, createAnalyticsEvent, scope, startNewConversation, accountId, push, addVirtualAgentMessage]);

    const handleKeyDown = useCallback((onSubmit: () => void) => {
        // TODO: handle this better see if we can use existing components or ref to achieve this
        return (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const currentActive = document.activeElement;
                currentActive === document.getElementById('conversation-dropdown-box-option-1')
                    ? focus('conversation-dropdown-box-option-2')
                    : focus('conversation-dropdown-box-option-1');
            }
            if (event.key === 'Enter') {
                onSubmit();
            }
        };
    }, []);

    useEffect(() => {
        if (isUnifiedHelpInJSMEnabled() && scope === 'helpCenter') {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            setProviderAuthStatus();
        }
    }, [scope, setProviderAuthStatus]);

    useEffect(() => {
        // If in a portal scope and the AI option is enabled, we'll show the VA conversation drawer when the user searches with AI, so we can preload it early
        if (isJsmVirtualAgentOnPortalEnabled() && scope === 'portal' && isAtlassianIntelligenceOptionShown) {
            return VirtualAgentConversationDrawer.preload();
        }
        return undefined;
    }, [scope, isAtlassianIntelligenceOptionShown]);

    return (
        <>
            <Popup
                isOpen={isChoiceOpen}
                onClose={() => setIsChoiceOpen(false)}
                placement="bottom-start"
                // eslint-disable-next-line jsx-a11y/no-autofocus -- need to disable autofocus
                autoFocus={false}
                shouldRenderToParent
                trigger={(triggerProps) => (
                    <Box
                        {...triggerProps}
                        xcss={searchBoxStyles}
                        style={{
                            // We need to manually use different token fallbacks here because the contrast isn't good enough with the default ones, while Portal is not yet using tokens. xcss doesn't allow specifying a token(...) as a value hence needing to use style prop
                            // These should be removed once Portal has finished migrating to using tokens
                            borderColor: token('color.border.input', '#dee0e4'),
                            backgroundColor: token('color.background.input', 'white'),
                        }}
                    >
                        <WidthObserver setWidth={setSearchBoxWidth} />
                        <Inline>
                            <SearchIconWrapper>
                                <SearchIcon primaryColor={token('color.icon.subtle', '#6B778C')} label="" />
                            </SearchIconWrapper>
                            <Input
                                type="text"
                                autoComplete="off"
                                spellCheck={false}
                                aria-label={
                                    isAtlassianIntelligenceOptionShown
                                        ? formatMessage(messages.inputAriaLabel)
                                        : formatMessage(messages.inputAriaLabelNoAI)
                                }
                                placeholder={
                                    isAtlassianIntelligenceOptionShown
                                        ? formatMessage(messages.inputPlaceholder)
                                        : formatMessage(messages.inputPlaceholderNoAI)
                                }
                                onChange={openChoicesOnChange}
                                onKeyDown={handleKeyDown(
                                    isAtlassianIntelligenceOptionShown ? onAskAtlassianIntelligence : onSeeSearchResults
                                )}
                                value={searchQuery}
                            />
                            {searchQuery.trim() !== '' && (
                                <CrossIconButton>
                                    <Button
                                        appearance="subtle"
                                        aria-label={formatMessage(messages.clearLabel)}
                                        iconBefore={<CrossIcon label="" size="small" />}
                                        onClick={handleCrossClick}
                                    />
                                </CrossIconButton>
                            )}
                        </Inline>
                    </Box>
                )}
                content={() => (
                    <Box style={{ width: searchBoxWidth }}>
                        <Stack grow="fill">
                            {isAtlassianIntelligenceOptionShown && (
                                <DropDownCard
                                    aria-label={formatMessage(messages.askAtlassianIntelligence)}
                                    onClick={onAskAtlassianIntelligence}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={handleKeyDown(onAskAtlassianIntelligence)}
                                    id={'conversation-dropdown-box-option-1'}
                                >
                                    <img src={atlassianIntelligenceIconUrl} alt="" />
                                    <QueryText>{searchQuery}</QueryText>
                                    <ButtonText>{formatMessage(messages.askAtlassianIntelligence)}</ButtonText>
                                    <EnterIcon src={enterIconUrl} alt="" />
                                </DropDownCard>
                            )}
                            <DropDownCard
                                aria-label={formatMessage(messages.seeSearchResults)}
                                onClick={onSeeSearchResults}
                                role="button"
                                tabIndex={0}
                                onKeyDown={handleKeyDown(onSeeSearchResults)}
                                id={'conversation-dropdown-box-option-2'}
                            >
                                <img src={allResultsSearchIcon} alt="" />
                                <QueryText>{searchQuery}</QueryText>
                                <ButtonText>{formatMessage(messages.seeSearchResults)}</ButtonText>
                                {!isAtlassianIntelligenceOptionShown && <EnterIcon src={enterIconUrl} alt="" />}
                            </DropDownCard>
                        </Stack>
                    </Box>
                )}
            />
            {isConversationOpen && scope === 'helpCenter' && (
                <SmartCardProvider>
                    <ConversationExperience />
                </SmartCardProvider>
            )}
            {isConversationOpen && scope === 'portal' && projectId !== undefined && (
                <VirtualAgentConversationDrawer
                    projectId={projectId}
                    onClose={() => {
                        setIsConversationOpen(false);
                    }}
                />
            )}
        </>
    );
};

const SearchTextboxV2WithConversationStoreContainer = (props: ComponentPropsWithoutRef<typeof SearchTextboxV2>) => {
    const conversationProvider = useVirtualAgentConversationProvider({ projectId: props.projectId });
    return (
        <ConversationStateContainer conversationProvider={conversationProvider}>
            <SearchTextboxV2 {...props} />
        </ConversationStateContainer>
    );
};

export default connect(
    createSelector(
        getAccountId,
        (state: State, props: ExternalProps) => getProjectId(state, toNumber(props.portalId)),
        (accountId, projectId) => ({ accountId, projectId })
    ),
    {},
    (stateProps, dispatchProps, { portalId, ...otherOwnProps }) => ({
        ...stateProps,
        ...dispatchProps,
        ...otherOwnProps,
        portalId: toNumber(portalId),
    })
)(
    withAnalyticsContext({
        component: ScreenName.HELP_CENTER_SEARCH,
    })(SearchTextboxV2WithConversationStoreContainer)
);

const searchBoxStyles = xcss({
    position: 'relative',
    backgroundColor: 'color.background.input',
    borderStyle: 'solid',
    borderWidth: 'border.width',
    borderColor: 'color.border.input',
    borderRadius: 'border.radius.100',
    ':focus-within': {
        outlineStyle: 'solid',
        outlineWidth: 'border.width.outline',
        outlineColor: 'color.border.focused',
    },
});

const Input = styled.input`
    ${SEARCH_INPUT_FONT_SIZE};
    ${mixins.placeholder(token('color.text.subtlest', '#6B778C'))};
    background-color: ${token('color.background.input', 'white')};
    border-radius: ${token('border.radius.100', '3px')};
    padding-top: ${grid.multiple(2.125).px};
    padding-bottom: ${grid.multiple(2.125).px};
    padding-right: ${token('space.600', '48px')};
    padding-left: ${token('space.050', '4px')};
    flex-grow: 1;
    border: 0;
    outline: none;
    color: ${token('color.text', colors.N800)};
`;

const CrossIconButton = styled.div`
    position: absolute;
    padding: 0 ${token('space.100', '8px')};
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
`;

const SearchIconWrapper = styled.div`
    padding: 0 ${token('space.200', '16px')};
    height: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    align-self: center;
    padding-right: ${token('space.050', '4px')};
`;

const DropDownCard = styled.div`
    display: flex;
    flex-direction: row;
    height: ${token('space.600', '48px')};
    width: 100%;
    justify-content: space-between;
    align-items: center;
    padding-block: ${token('space.400', '32px')};
    padding-inline: ${token('space.300', '24px')};
    box-sizing: border-box;
    cursor: pointer;
    overflow: hidden;
    border-radius: ${token('space.050', '4px')};

    &:hover {
        background-color: ${token('elevation.surface.overlay.hovered', '#0000000f')};
    }

    &:active {
        background-color: ${token('elevation.surface.overlay.pressed', '#0000000f')};
    }

    &:focus {
        outline-style: solid;
        outline-width: ${token('border.width.outline', '2px')};
        outline-color: ${token('color.border.focused', '#6495ed')};
    }
`;

const QueryText = styled.div`
    flex-grow: 1;
    margin-inline: ${token('space.150', '12px')};
    overflow: hidden;
    text-overflow: ellipsis;
    text-wrap: nowrap;
    color: ${token('color.text', colors.N800)};
`;

const ButtonText = styled.p`
    color: ${token('color.text.subtlest', '#626F86')};
    margin: 0;
    text-wrap: nowrap;
`;

const EnterIcon = styled.img`
    margin-left: ${token('space.150', '12px')};
`;
